'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { BarChart3, TrendingUp, DollarSign, Truck, Star, Award, Clock, AlertTriangle, CheckCircle2, ArrowLeft,  } from 'lucide-react';
import { MOCK_SUPPLIERS, formatUGX, getPerformanceLevel } from '../../components/suppliersData';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend,  } from 'recharts';
import Icon from '@/components/ui/AppIcon';


// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, icon: Icon, color }: { label: string; value: string | number; sub?: string; icon: React.ElementType; color: string }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${color}`}>
        <Icon size={16} className="text-white" />
      </div>
      <div className="text-2xl font-black text-gray-900 mb-0.5">{value}</div>
      <div className="text-xs font-semibold text-gray-500">{label}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

// ─── Chart Data ───────────────────────────────────────────────────────────────

const SPEND_TREND = [
  { month: 'Apr', spend: 38500000 },
  { month: 'May', spend: 42000000 },
  { month: 'Jun', spend: 35800000 },
  { month: 'Jul', spend: 48200000 },
  { month: 'Aug', spend: 44500000 },
  { month: 'Sep', spend: 29500000 },
];

const SPEND_BY_SUPPLIER = MOCK_SUPPLIERS.map(s => ({ name: s.name.split(' ')[0], spend: s.totalSpend })).sort((a, b) => b.spend - a.spend);

const DELIVERY_PERF = MOCK_SUPPLIERS.filter(s => s.onTimeDeliveryPct).map(s => ({
  name: s.name.split(' ')[0],
  onTime: s.onTimeDeliveryPct ?? 0,
  late: 100 - (s.onTimeDeliveryPct ?? 0),
}));

const QUALITY_PERF = MOCK_SUPPLIERS.filter(s => s.qualityScore).map(s => ({
  name: s.name.split(' ')[0],
  score: s.qualityScore ?? 0,
}));

const STATUS_DIST = [
  { name: 'Active', value: MOCK_SUPPLIERS.filter(s => s.status === 'active').length, color: '#10b981' },
  { name: 'Preferred', value: MOCK_SUPPLIERS.filter(s => s.status === 'preferred').length, color: '#8b5cf6' },
  { name: 'Pending', value: MOCK_SUPPLIERS.filter(s => s.status === 'pending_approval' || s.status === 'pending_review').length, color: '#f59e0b' },
  { name: 'Inactive', value: MOCK_SUPPLIERS.filter(s => s.status === 'inactive' || s.status === 'suspended').length, color: '#ef4444' },
];

const RISK_INDICATORS = [
  { supplier: 'Farm Equipment Solutions Ltd', risk: 'Expiring Documents', level: 'medium', detail: 'Trading license expires Oct 2026' },
  { supplier: 'Nile Agro Chemicals Ltd', risk: 'Pending Compliance', level: 'high', detail: 'Documents pending verification' },
  { supplier: 'Eastern Hatcheries Ltd', risk: 'Expiring Documents', level: 'medium', detail: 'Veterinary license expires Oct 2026' },
];

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SupplierAnalyticsContent() {
  const totalSpend = MOCK_SUPPLIERS.reduce((s, x) => s + x.totalSpend, 0);
  const totalBalance = MOCK_SUPPLIERS.reduce((s, x) => s + x.outstandingBalance, 0);
  const avgScore = Math.round(MOCK_SUPPLIERS.reduce((s, x) => s + (x.overallScore ?? 0), 0) / MOCK_SUPPLIERS.length);
  const avgDelivery = Math.round(MOCK_SUPPLIERS.filter(s => s.onTimeDeliveryPct).reduce((s, x) => s + (x.onTimeDeliveryPct ?? 0), 0) / MOCK_SUPPLIERS.filter(s => s.onTimeDeliveryPct).length);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <Link href="/suppliers" className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <ArrowLeft size={16} className="text-gray-500" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
              <BarChart3 size={15} className="text-orange-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Supplier Analytics</h1>
              <p className="text-xs text-gray-400">Performance, spend, risk, and relationship insights</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6 max-w-screen-2xl mx-auto">

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          <KpiCard label="Total Suppliers" value={MOCK_SUPPLIERS.length} icon={Truck} color="bg-gray-700" />
          <KpiCard label="Active Suppliers" value={MOCK_SUPPLIERS.filter(s => s.status === 'active' || s.status === 'preferred').length} icon={CheckCircle2} color="bg-green-500" />
          <KpiCard label="Preferred Suppliers" value={MOCK_SUPPLIERS.filter(s => s.preferred).length} icon={Award} color="bg-purple-500" />
          <KpiCard label="Avg Supplier Score" value={`${avgScore}/100`} icon={BarChart3} color="bg-orange-500" />
          <KpiCard label="Total Spend" value={`UGX ${(totalSpend / 1000000).toFixed(0)}M`} icon={DollarSign} color="bg-blue-500" />
          <KpiCard label="Outstanding Payables" value={`UGX ${(totalBalance / 1000000).toFixed(1)}M`} icon={TrendingUp} color="bg-red-500" />
          <KpiCard label="Avg Delivery Time" value="3.2 days" icon={Clock} color="bg-teal-500" />
          <KpiCard label="Avg On-Time Delivery" value={`${avgDelivery}%`} icon={Truck} color="bg-indigo-500" />
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Spend Trend */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-1">Supplier Spend Trend</h3>
            <p className="text-xs text-gray-400 mb-4">Monthly procurement spend (UGX)</p>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={SPEND_TREND}>
                <defs>
                  <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000000).toFixed(0)}M`} />
                <Tooltip formatter={(v: number) => [`UGX ${(v / 1000000).toFixed(1)}M`, 'Spend']} />
                <Area type="monotone" dataKey="spend" stroke="#f97316" strokeWidth={2} fill="url(#sg)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Status Distribution */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Supplier Status Distribution</h3>
            <ResponsiveContainer width="100%" height={140}>
              <PieChart>
                <Pie data={STATUS_DIST} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" paddingAngle={2}>
                  {STATUS_DIST.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5 mt-2">
              {STATUS_DIST.map(s => (
                <div key={s.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                    <span className="text-gray-600">{s.name}</span>
                  </div>
                  <span className="font-bold text-gray-700">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Spend by Supplier */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Spend by Supplier</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={SPEND_BY_SUPPLIER} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000000).toFixed(0)}M`} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={80} />
                <Tooltip formatter={(v: number) => [`UGX ${(v / 1000000).toFixed(1)}M`]} />
                <Bar dataKey="spend" fill="#f97316" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Delivery Performance */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Delivery Performance</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={DELIVERY_PERF}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Bar dataKey="onTime" name="On-Time %" fill="#10b981" radius={[4, 4, 0, 0]} stackId="a" />
                <Bar dataKey="late" name="Late %" fill="#f97316" radius={[4, 4, 0, 0]} stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quality Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Quality Performance by Supplier</h3>
            <div className="space-y-3">
              {MOCK_SUPPLIERS.filter(s => s.qualityScore).sort((a, b) => (b.qualityScore ?? 0) - (a.qualityScore ?? 0)).map(s => {
                const perf = getPerformanceLevel(s.qualityScore ?? 0);
                return (
                  <div key={s.id}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-gray-700 truncate">{s.name}</span>
                      <span className={`text-xs font-bold ${perf.color}`}>{s.qualityScore}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full bg-green-400" style={{ width: `${s.qualityScore}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Risk Indicators */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Risk Indicators</h3>
            <div className="space-y-3">
              {RISK_INDICATORS.map((r, i) => (
                <div key={i} className={`flex items-start gap-3 p-3 rounded-xl ${r.level === 'high' ? 'bg-red-50' : r.level === 'medium' ? 'bg-amber-50' : 'bg-gray-50'}`}>
                  <AlertTriangle size={14} className={r.level === 'high' ? 'text-red-500' : 'text-amber-500'} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-gray-900">{r.supplier}</div>
                    <div className="text-xs text-gray-600">{r.risk}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{r.detail}</div>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${r.level === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {r.level.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>

            {/* Concentration Warning */}
            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle size={12} className="text-orange-600" />
                <span className="text-xs font-bold text-orange-700">Supplier Concentration Warning</span>
              </div>
              <p className="text-xs text-orange-600">
                51% of Feed procurement currently depends on ABC Feeds Uganda Ltd. Consider diversifying feed suppliers to reduce risk.
              </p>
            </div>
          </div>
        </div>

        {/* Outstanding Payables */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Outstanding Payables by Supplier</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 font-semibold text-gray-500">Supplier</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-500">Category</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-500">Total Spend</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-500">Outstanding</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-500">Open POs</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-500">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {MOCK_SUPPLIERS.filter(s => s.outstandingBalance > 0).map(s => (
                  <tr key={s.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 font-semibold text-gray-800">
                      <Link href={`/suppliers/${s.id}`} className="hover:text-orange-600 transition-colors">{s.name}</Link>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{s.categories[0]}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{formatUGX(s.totalSpend)}</td>
                    <td className="px-4 py-3 text-right font-bold text-orange-600">{formatUGX(s.outstandingBalance)}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{s.openPOs}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Star size={10} className="text-amber-400 fill-amber-400" />
                        <span className="font-bold text-gray-700">{s.rating}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
